export const STOCK_STATUS = {
  PLANEJADO: "PLANEJADO",
  DISPONIVEL: "DISPONIVEL",
  EM_USO: "EM_USO",
  UTILIZADO: "UTILIZADO",
};

export const getStockItemId = (item) => item?.id ?? item?._id;

export const toNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;

  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatPtNumber = (value, digits = 2) => {
  if (value === null || value === undefined) return "";

  const num = Number(value);
  if (!Number.isFinite(num)) return "";

  try {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(num);
  } catch {
    return String(Number(num.toFixed(digits))).replace(".", ",");
  }
};

export const formatShortDate = (value) => {
  if (!value) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(value));
  } catch {
    return value;
  }
};

export const normalizeName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// ---------------- STATUS ----------------

export const getStatusBadgeStyle = (status) => {
  switch (status) {
    case STOCK_STATUS.PLANEJADO:
      return "bg-gray-100 text-gray-700 border-gray-200";
    case STOCK_STATUS.DISPONIVEL:
      return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case STOCK_STATUS.EM_USO:
      return "bg-green-100 text-green-800 border-green-200";
    case STOCK_STATUS.UTILIZADO:
      return "bg-slate-100 text-slate-600 border-slate-200";
    default:
      return "bg-gray-100 text-gray-700 border-gray-200";
  }
};

export const getStatusLabel = (status) => {
  switch (status) {
    case STOCK_STATUS.PLANEJADO:
      return "Planejado";
    case STOCK_STATUS.DISPONIVEL:
      return "Em estoque";
    case STOCK_STATUS.EM_USO:
      return "Em uso";
    case STOCK_STATUS.UTILIZADO:
      return "Arquivado";
    default:
      return "Não informado";
  }
};

// ---------------- TIPOS ----------------

export const getMedicationType = (item) =>
  String(item?.medicationType || item?.medication_type || "")
    .trim()
    .toUpperCase();

export const isUnitMedication = (item) => {
  const type = getMedicationType(item);
  return type === "COMPRIMIDO" || type === "CAPSULA";
};

export const getUnitLabel = (item) => {
  const type = getMedicationType(item);

  if (type === "CAPSULA") return "cápsulas";
  if (type === "COMPRIMIDO") return "comprimidos";

  const raw = String(item?.unitLabel || item?.unit_label || "").trim();
  return raw || "unidades";
};

// ---------------- CÁLCULOS ----------------

export const calculateMgPerMl = ({ mgBase, mlBase, mgPerMl }) => {
  const savedMgPerMl = toNumber(mgPerMl);
  if (savedMgPerMl != null) return savedMgPerMl;

  const mg = toNumber(mgBase);
  const ml = toNumber(mlBase);

  if (mg == null || ml == null || ml <= 0) return null;
  return mg / ml;
};

export const calculateTotalMg = ({ mgBase, mlBase, mgPerMl, totalVolumeMl }) => {
  const resolvedMgPerMl = calculateMgPerMl({ mgBase, mlBase, mgPerMl });
  const totalVolume = toNumber(totalVolumeMl);

  if (resolvedMgPerMl == null || totalVolume == null || totalVolume <= 0) {
    return null;
  }

  return resolvedMgPerMl * totalVolume;
};

// ---------------- LABELS ----------------

export const getConcentrationLabel = (item) => {
  if (isUnitMedication(item)) {
    const mgPerUnit = toNumber(item?.mgPerUnit);
    if (mgPerUnit != null) {
      return `${formatPtNumber(mgPerUnit)} mg por unidade`;
    }
    return "Não informado";
  }

  const mgBase = toNumber(item?.mgBase);
  const mlBase = toNumber(item?.mlBase);
  const resolvedMgPerMl = calculateMgPerMl(item);

  if (mgBase != null && mlBase != null) {
    return `${formatPtNumber(mgBase)} mg/${formatPtNumber(mlBase)} mL`;
  }

  if (resolvedMgPerMl != null) {
    return `${formatPtNumber(resolvedMgPerMl, 2)} mg/mL`;
  }

  return "Não informado";
};

export const getPackageAmountLabel = (item) => {
  if (isUnitMedication(item)) {
    const totalUnits = toNumber(item?.totalUnits);
    const unitLabel = getUnitLabel(item);

    if (totalUnits != null) {
      return `${formatPtNumber(totalUnits, 0)} ${unitLabel}`;
    }

    return "Não informado";
  }

  const totalVol = toNumber(item?.totalVolumeMl);
  if (totalVol != null) {
    return `${formatPtNumber(totalVol)} mL`;
  }

  return "Não informado";
};

export const getStockLabel = (item) => {
  if (isUnitMedication(item)) {
    const remainingUnits = toNumber(item?.remainingUnits);
    const totalUnits = toNumber(item?.totalUnits);
    const unitLabel = getUnitLabel(item);

    if (remainingUnits != null) {
      return `${formatPtNumber(remainingUnits, 0)} ${unitLabel}`;
    }

    if (totalUnits != null) {
      return `${formatPtNumber(totalUnits, 0)} ${unitLabel}`;
    }

    const remainingMg = toNumber(item?.remainingMg);
    if (remainingMg != null) {
      return `${formatPtNumber(remainingMg, 2)} mg`;
    }

    return "Não informado";
  }

  const remaining = toNumber(item?.remainingMg);
  if (remaining != null) {
    return `${formatPtNumber(remaining, 2)} mg`;
  }

  const totalMg = calculateTotalMg(item);
  if (totalMg != null) {
    return `${formatPtNumber(totalMg, 2)} mg`;
  }

  return "Não informado";
};

// ---------------- AGRUPAMENTO ----------------

export const hasUsableStockItem = (items = []) =>
  items.some((item) => {
    const status = item?.status;
    return status === STOCK_STATUS.DISPONIVEL || status === STOCK_STATUS.EM_USO;
  });

export const groupStockItemsByMedication = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    const rawName =
      (item?.medicationName || item?.name || "").trim() || "Medicamento";

    const key = normalizeName(rawName);

    if (!map.has(key)) {
      map.set(key, {
        key,
        name: rawName,
        items: [],
      });
    }

    map.get(key).items.push(item);
  });

  const priority = {
    [STOCK_STATUS.EM_USO]: 0,
    [STOCK_STATUS.DISPONIVEL]: 1,
    [STOCK_STATUS.PLANEJADO]: 2,
    [STOCK_STATUS.UTILIZADO]: 3,
  };

  return Array.from(map.values()).map((group) => {
    const sortedItems = [...group.items].sort((a, b) => {
      const aStatus = a?.status || "";
      const bStatus = b?.status || "";
      const statusDiff = (priority[aStatus] ?? 99) - (priority[bStatus] ?? 99);

      if (statusDiff !== 0) return statusDiff;

      const aDate = new Date(a?.startDate || 0).getTime();
      const bDate = new Date(b?.startDate || 0).getTime();
      return bDate - aDate;
    });

    return {
      ...group,
      items: sortedItems,
      summary: getGroupSummary(sortedItems),
    };
  });
};

export const getGroupSummary = (items = []) => {
  const usableItems = items.filter(
    (item) =>
      item?.status === STOCK_STATUS.DISPONIVEL ||
      item?.status === STOCK_STATUS.EM_USO
  );

  const hasUnitMedication = usableItems.some((item) => isUnitMedication(item));

  if (hasUnitMedication) {
    const totalRemainingUnits = usableItems.reduce((acc, item) => {
      if (!isUnitMedication(item)) return acc;

      const remainingUnits = toNumber(item?.remainingUnits);
      if (remainingUnits != null) return acc + remainingUnits;

      const totalUnits = toNumber(item?.totalUnits);
      if (totalUnits != null) return acc + totalUnits;

      return acc;
    }, 0);

    const labelSuffix = usableItems.find((item) => isUnitMedication(item))
      ? getUnitLabel(usableItems.find((item) => isUnitMedication(item)))
      : "unidades";

    const hasEmUso = items.some((item) => item?.status === STOCK_STATUS.EM_USO);
    const hasDisponivel = items.some(
      (item) => item?.status === STOCK_STATUS.DISPONIVEL
    );
    const hasPlanejado = items.some(
      (item) => item?.status === STOCK_STATUS.PLANEJADO
    );

    if (hasEmUso) {
      return {
        label:
          totalRemainingUnits > 0
            ? `${formatPtNumber(totalRemainingUnits, 0)} ${labelSuffix}`
            : "Em uso",
        color: "text-[#43C463]",
      };
    }

    if (hasDisponivel) {
      return {
        label:
          totalRemainingUnits > 0
            ? `${formatPtNumber(totalRemainingUnits, 0)} ${labelSuffix}`
            : "Em estoque",
        color: "text-[#43C463]",
      };
    }

    if (hasPlanejado) {
      return {
        label: "Planejado",
        color: "text-[#E49322]",
      };
    }

    return {
      label: "Sem estoque",
      color: "text-[#FF3B30]",
    };
  }

  const totalRemaining = usableItems.reduce((acc, item) => {
    const remaining = toNumber(item?.remainingMg);
    if (remaining != null) return acc + remaining;

    const totalMg = calculateTotalMg(item);
    if (totalMg != null) return acc + totalMg;

    return acc;
  }, 0);

  const hasEmUso = items.some((item) => item?.status === STOCK_STATUS.EM_USO);
  const hasDisponivel = items.some(
    (item) => item?.status === STOCK_STATUS.DISPONIVEL
  );
  const hasPlanejado = items.some(
    (item) => item?.status === STOCK_STATUS.PLANEJADO
  );

  if (hasEmUso) {
    return {
      label: totalRemaining > 0 ? `${formatPtNumber(totalRemaining, 0)} mg` : "Em uso",
      color: "text-[#43C463]",
    };
  }

  if (hasDisponivel) {
    return {
      label:
        totalRemaining > 0 ? `${formatPtNumber(totalRemaining, 0)} mg` : "Em estoque",
      color: "text-[#43C463]",
    };
  }

  if (hasPlanejado) {
    return {
      label: "Planejado",
      color: "text-[#E49322]",
    };
  }

  return {
    label: "Sem estoque",
    color: "text-[#FF3B30]",
  };
};